import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Layout } from "../components/Navbar";
import { AddressAutocomplete } from "../components/AddressAutocomplete";
import { api, getUser } from "../api";
import { toast } from "sonner";

export default function CreateEvent() {
  const { t, i18n } = useTranslation();
  const u = getUser();
  const nav = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loaded, setLoaded] = useState(!isEdit);
  const [f, setF] = useState({ title:"", description:"", status:"published", price_cents:"", visibility:"public", access_password:"", slots:[{ start_at:"", capacity:20 }], is_diagnostic:false, modality:"", exam_type:"", device_id:"" });
  const [sel, setSel] = useState(null); // indirizzo selezionato dai suggerimenti (lat/lng restano tecnici)
  const [imageUrl, setImageUrl] = useState(""); // locandina evento (opzionale) — URL servito da /api/event-images/{id}
  const [imgBusy, setImgBusy] = useState(false);
  const [devices, setDevices] = useState(null); // null = non caricati; [] = clinico senza dispositivi; array = dispositivi
  useEffect(() => { api.get("/pam-connect/web-devices").then(({ data }) => setDevices(data.devices || [])).catch(() => setDevices(null)); }, []);
  const onPickImage = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    setImgBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/org/event-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImageUrl(data.url);
    } catch { toast.error(t("events.error_generic")); }
    finally { setImgBusy(false); ev.target.value = ""; }
  };
  useEffect(() => {
    if (!isEdit) return;
    let ignore = false;  // StrictMode double-run: mai sovrascrivere dopo lo smontaggio
    api.get(`/events/${id}`).then(({data}) => {
      if (ignore) return;
      setF({
        title: data.title, description: data.description, status: data.status, price_cents: data.price_cents ? (data.price_cents/100) : "",
        visibility: data.visibility || "public", access_password: "",
        slots: (data.slots||[]).map(s => ({ id: s.id, start_at: (s.start_at||"").slice(0,16), capacity: s.capacity })),
        is_diagnostic: !!data.is_diagnostic, modality: data.modality || "", exam_type: data.exam_type || "", device_id: data.device_id || "",
      });
      // Prefill: vecchi eventi con citta'/coordinate ma senza indirizzo completo restano validi, nessun dato perso
      setSel({ address: data.address || "", city: data.city || "", region: data.region || "", country: data.country || "", lat: data.lat, lng: data.lng });
      setImageUrl(data.image_url || "");
      setLoaded(true);  // il form appare solo dopo il prefill: nessuna race con l'input utente
    }).catch(() => toast.error(t("events.event_not_open")));
    return () => { ignore = true; };
  }, [id]);
  const submit = async (e) => {
    e.preventDefault();
    if (!sel?.address) { toast.error(t("events.address_required")); return; }
    if (f.is_diagnostic && !f.device_id) { toast.error(t("events.diag_device_required")); return; }
    try {
      const clean = { title: f.title, description: f.description, status: f.status,
        price_cents: f.price_cents ? Math.round(Number(f.price_cents)*100) : 0,
        visibility: f.visibility, access_password: f.visibility === "private" ? (f.access_password || "") : "",
        address: sel.address, city: sel.city || null, region: sel.region || null, country: sel.country || null,
        lat: sel.lat, lng: sel.lng, image_url: imageUrl || null,
        is_diagnostic: f.is_diagnostic, modality: f.is_diagnostic ? (f.modality || null) : null,
        exam_type: f.is_diagnostic ? (f.exam_type || null) : null, device_id: f.is_diagnostic ? (f.device_id || null) : null,
        slots: f.slots.filter(s => s.start_at).map(s => ({ ...s, capacity: Number(s.capacity) })) };
      if (isEdit) {
        await api.patch(`/events/${id}`, clean);
        toast.success(t("events.saved"));
        nav("/my-events");
      } else {
        const { data } = await api.post("/events", clean);
        toast.success(t("events.created"));
        nav(`/events/${data.id}`);
      }
    } catch(err) { toast.error(err.response?.data?.detail || t("events.error_generic")); }
  };
  const addSlot = () => setF({ ...f, slots: [...f.slots, { start_at:"", capacity:20 }] });
  if (u?.role !== "organization") return <Layout><div className="yp-container max-w-2xl py-8" data-testid="create-event-forbidden">{t("events.only_org")}</div></Layout>;
  if (isEdit && !loaded) return <Layout><div className="yp-container max-w-2xl py-8" data-testid="event-edit-loading">{t("events.edit_title")}...</div></Layout>;
  return (<Layout><div className="yp-container max-w-2xl py-8">
    <h1 className="text-3xl font-bold mb-6">{isEdit ? t("events.edit_title") : t("events.create")}</h1>
    <form onSubmit={submit} className="yp-card p-6 space-y-4" data-testid="event-create-form">
      <input required className="yp-input" placeholder={t("events.title_ph")} value={f.title} onChange={e=>setF({...f,title:e.target.value})} data-testid="event-title" />
      <textarea required className="yp-input" rows={4} placeholder={t("events.desc_ph")} value={f.description} onChange={e=>setF({...f,description:e.target.value})} data-testid="event-desc" />
      <AddressAutocomplete value={sel} onSelect={setSel} />
      <div data-testid="event-image-row">
        <label className="text-sm font-medium">{{it:"Immagine (locandina)",en:"Image (flyer)",de:"Bild (Plakat)",fr:"Image (affiche)"}[i18n.language]||"Immagine (locandina)"}</label>
        {imageUrl ? (
          <div className="mt-2 space-y-2" data-testid="event-image-preview">
            <img src={imageUrl.startsWith("http") ? imageUrl : `${process.env.REACT_APP_BACKEND_URL}${imageUrl}`} alt="" className="w-full max-h-56 object-cover rounded-lg border border-slate-200" />
            <div className="flex gap-2">
              <label className="yp-btn yp-btn-outline !py-1 !px-3 text-xs cursor-pointer" data-testid="event-image-replace">{imgBusy ? t("pd.uploading") : t("events.change_image")}<input type="file" accept="image/*" className="hidden" onChange={onPickImage} disabled={imgBusy} /></label>
              <button type="button" className="yp-btn yp-btn-ghost !py-1 !px-3 text-xs text-red-600" onClick={() => setImageUrl("")} data-testid="event-image-remove">{t("events.remove_image")}</button>
            </div>
          </div>
        ) : (
          <div className="mt-2">
            <label className="inline-flex yp-btn yp-btn-outline !py-1 !px-3 text-xs cursor-pointer" data-testid="event-image-attach">{imgBusy ? t("pd.uploading") : `+ ${t("events.attach_image")}`}<input type="file" accept="image/*" className="hidden" onChange={onPickImage} disabled={imgBusy} /></label>
          </div>
        )}
      </div>
      <div>
        <label className="text-sm font-medium">{t("events.slots")}</label>
        {f.slots.map((s,i) => (
          <div key={i} className="grid grid-cols-2 gap-2 mt-2">
            <input type="datetime-local" className="yp-input" value={s.start_at} onChange={e=>{ const n=[...f.slots]; n[i].start_at=e.target.value; setF({...f,slots:n}); }} data-testid={`slot-start-${i}`} />
            <input type="number" className="yp-input" placeholder={t("events.capacity")} value={s.capacity} onChange={e=>{ const n=[...f.slots]; n[i].capacity=e.target.value; setF({...f,slots:n}); }} data-testid={`slot-cap-${i}`} />
          </div>))}
        <button type="button" className="yp-btn yp-btn-ghost mt-2 !text-xs" onClick={addSlot} data-testid="slot-add">+ {t("events.slots")}</button>
      </div>
      <div className="grid grid-cols-2 gap-3" data-testid="event-price-row">
        <div>
          <label className="text-sm font-medium">{{it:"Tipo",en:"Type",de:"Typ",fr:"Type"}[i18n.language]||"Tipo"}</label>
          <select className="yp-input" value={f.price_cents!=="" ? "paid" : "free"} onChange={e=>setF({...f, price_cents: e.target.value==="paid" ? (f.price_cents || "0") : ""})} data-testid="event-paid-toggle">
            <option value="free">{{it:"Gratuito",en:"Free",de:"Kostenlos",fr:"Gratuit"}[i18n.language]||"Gratuito"}</option>
            <option value="paid">{{it:"A pagamento",en:"Paid",de:"Kostenpflichtig",fr:"Payant"}[i18n.language]||"A pagamento"}</option>
          </select>
        </div>
        <div>
          <label className="text-sm font-medium">{t("inperson.price")}</label>
          <input type="number" step="0.01" min="0" className="yp-input" value={f.price_cents} onChange={e=>setF({...f, price_cents:e.target.value})} disabled={f.price_cents===""} placeholder="0.00" data-testid="event-price-input" />
        </div>
      </div>
      <select className="yp-input" value={f.status} onChange={e=>setF({...f,status:e.target.value})} data-testid="event-status-select">
        <option value="published">{t("events.status_published")}</option><option value="draft">{t("events.status_draft")}</option>
      </select>
      {devices !== null && (
        <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="event-diagnostic-row">
          <label className="flex items-center gap-2 text-sm font-medium">
            <input type="checkbox" checked={f.is_diagnostic} onChange={e=>setF({...f, is_diagnostic: e.target.checked})} data-testid="event-diagnostic-check" />
            {t("events.diagnostic")}
          </label>
          {f.is_diagnostic && (<div className="space-y-2 pl-6" data-testid="event-diagnostic-panel">
            <p className="text-xs text-slate-500">{t("events.diag_hint")}</p>
            {devices.length === 0 ? (
              <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="event-diag-no-devices">{t("events.diag_no_devices")}</div>
            ) : (<>
              <div>
                <label className="text-sm font-medium">{t("events.diag_device")}</label>
                <select className="yp-input" value={f.device_id} onChange={e=>setF({...f, device_id: e.target.value})} data-testid="event-diag-device">
                  <option value="">{t("events.diag_device_ph")}</option>
                  {devices.map(d => <option key={d.device_id} value={d.device_id}>{[d.display_name, d.modality, d.connector_label].filter(Boolean).join(" · ")}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input className="yp-input" placeholder={t("events.diag_modality")} value={f.modality} onChange={e=>setF({...f, modality: e.target.value})} data-testid="event-diag-modality" />
                <input className="yp-input" placeholder={t("events.diag_exam_type")} value={f.exam_type} onChange={e=>setF({...f, exam_type: e.target.value})} data-testid="event-diag-exam" />
              </div>
              <p className="text-[11px] text-slate-400">{t("events.diag_mwl_note")}</p>
            </>)}
          </div>)}
        </div>
      )}
      <div className="border-t border-slate-100 pt-4 space-y-3" data-testid="event-visibility-row">
        <label className="text-sm font-medium">{{it:"Visibilità",en:"Visibility",de:"Sichtbarkeit",fr:"Visibilité"}[i18n.language]||"Visibilità"}</label>
        <select className="yp-input" value={f.visibility} onChange={e=>setF({...f, visibility:e.target.value})} data-testid="event-visibility-select">
          <option value="public">{{it:"Pubblico — visibile su profilo e aree pubbliche",en:"Public — visible on profile and public areas",de:"Öffentlich — auf Profil und öffentlichen Bereichen sichtbar",fr:"Public — visible sur le profil et les espaces publics"}[i18n.language]}</option>
          <option value="private">{{it:"Privato — solo tramite link",en:"Private — link only",de:"Privat — nur per Link",fr:"Privé — via lien uniquement"}[i18n.language]}</option>
        </select>
        {f.visibility === "private" && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm" data-testid="event-protect-pw-row">
              <input type="checkbox" checked={f.access_password !== ""} onChange={e=>setF({...f, access_password: e.target.checked ? " " : ""})} data-testid="event-protect-pw-check" />
              {{it:"Proteggi con password",en:"Protect with password",de:"Mit Passwort schützen",fr:"Protéger par mot de passe"}[i18n.language]||"Proteggi con password"}
            </label>
            {f.access_password !== "" && (
              <input className="yp-input" type="text" value={f.access_password.trim()} onChange={e=>setF({...f, access_password:e.target.value})} placeholder={{it:"Password evento",en:"Event password",de:"Event-Passwort",fr:"Mot de passe de l'événement"}[i18n.language]||"Password evento"} data-testid="event-pw-input" />)}
          </div>)}
      </div>
      <button className="yp-btn yp-btn-primary w-full" type="submit" data-testid="event-submit">{isEdit ? t("events.save") : t("events.create")}</button>
    </form>
  </div></Layout>);
}
